import {
  PokemonType,
  SkillData,
  SkillType,
  SkillRangeType,
  StatusEffect,
  SkillEvent_Global,
} from "types/battle-engine/battle";
import { parseSkillIdToName } from "../utils/parse-id-to-name";
import { msToTicks } from "../utils/ms-to-ticks";

// ============================================================================
// +GLOBAL EFFECT SPELLS
// One global buff/debuff skill per Pokemon type (excluding NORMAL and FIRE which already have coverage)
// ============================================================================
export const [
  RAIN_DANCE,
  GRASSY_TERRAIN,
  ELECTRIC_TERRAIN,
  AURORA_VEIL,
  REFLECT,
  BULK_UP,
  VENOM_DRENCH,
  ROTOTILLER,
  TAILWIND,
  STICKY_WEB,
  STEALTH_ROCK,
  CURSE,
  DRAGON_DANCE,
  SNARL,
  GEAR_GRIND,
  MISTY_TERRAIN,
]: SkillData[] = [
  {
    id: "rain-dance",
    description:
      "Starts battle with 0 cooldown. Creates a rain dance that raises the speed of all allies.",
    starts_active: true,
    pokemon_type: PokemonType.WATER,
    type: SkillType.BUFF,
    status: [StatusEffect.SPEED_UP],
    status_duration_ticks: msToTicks(6000),
    cooldown_ticks: msToTicks(4000),

    probability: 1,
    additional_vfx: [
      {
        sprite_path: "/assets/spells/vfx/self_bubbles_000__20.png",
        sprite_scale: 1,
        sprite_alpha: 1,
        speed: -0.5,
        anchor_to_target: true,
        y_offset: -55,
      },
    ],
  },
  {
    id: "grassy-terrain",
    description: "Raises the defense of all allies.",
    pokemon_type: PokemonType.GRASS,
    type: SkillType.BUFF,
    status: [StatusEffect.DEFENSE_UP],
    status_duration_ticks: msToTicks(5000),
    cooldown_ticks: msToTicks(11000),
    probability: 1,
    additional_vfx: [
      {
        sprite_path: "/assets/spells/vfx/grassbuff__26.png",
        sprite_scale: 1.2,
        sprite_alpha: 1,
        speed: 0.25,
        anchor_to_target: true,
        y_offset: -50,
      },
    ],
  },
  {
    id: "electric-terrain",
    description: "Raises the attack of all allies.",
    pokemon_type: PokemonType.ELECTRIC,
    type: SkillType.BUFF,
    cooldown_ticks: msToTicks(5000),
    status_duration_ticks: msToTicks(5000),
    status: [StatusEffect.ATTACK_UP],
    probability: 1,
    additional_vfx: [
      {
        sprite_path: "/assets/spells/vfx/yellow-sparkles__11.png",
        sprite_scale: 1.5,
        sprite_alpha: 1,
        speed: 0.3,
        anchor_to_target: true,
        y_offset: -45,
      },
    ],
  },
  {
    id: "aurora-veil",
    description: "Raises the defense of all allies.",
    pokemon_type: PokemonType.ICE,
    type: SkillType.BUFF,
    status: [StatusEffect.DEFENSE_UP],
    status_duration_ticks: msToTicks(5000),
    cooldown_ticks: msToTicks(11000),
    probability: 1,
    additional_vfx: [
      {
        sprite_path: "/assets/spells/vfx/white-star-buff__12.png",
        sprite_scale: 1.2,
        sprite_alpha: 0.9,
        speed: 0.25,
        anchor_to_target: true,
        y_offset: -25,
      },
    ],
  },
  {
    id: "reflect",
    description: "Raises the defense of all allies.",
    pokemon_type: PokemonType.PSYCHIC,
    type: SkillType.BUFF,
    status: [StatusEffect.DEFENSE_UP],
    status_duration_ticks: msToTicks(5000),
    cooldown_ticks: msToTicks(11000),
    probability: 1,
    additional_vfx: [
      {
        sprite_path: "/assets/spells/vfx/galaxyorb__36.png",
        sprite_scale: 1.0,
        sprite_alpha: 0.85,
        speed: 0.2,
        anchor_to_target: true,
        y_offset: -60,
      },
    ],
  },
  {
    id: "bulk-up",
    description: "Raises the defense of all allies.",
    pokemon_type: PokemonType.FIGHTING,
    type: SkillType.BUFF,
    status: [StatusEffect.DEFENSE_UP],
    probability: 1,
    additional_vfx: [
      {
        sprite_path: "/assets/spells/vfx/whiteredbuff__21.png",
        sprite_scale: 1.3,
        sprite_alpha: 1,
        speed: 0.25,
        anchor_to_target: true,
        y_offset: -50,
      },
    ],
    starts_active: false,
  },
  {
    id: "venom-drench",
    description:
      "Lowers the attack of all enemies. If the target has SPEED DOWN, apply 2x 🟣POISON.",
    pokemon_type: PokemonType.POISON,
    type: SkillType.DEBUFF,
    status: [StatusEffect.ATTACK_DOWN],
    probability: 1,
    additional_vfx: [
      {
        sprite_path: "/assets/spells/vfx/poisontrap__13.png",
        sprite_scale: 1,
        sprite_alpha: 1,
        speed: 0.45,
        anchor_to_target: true,
        y_offset: 0,
      },
    ],
    event_checks: [
      {
        conditions: [
          {
            type: "TARGET_HAS_STATUS",
            status: StatusEffect.SPEED_DOWN,
          },
        ],
        effects: [
          {
            type: "APPLY_STATUS_TARGET",
            status_effects: [
              {
                status: StatusEffect.POISON,
                duration_ticks: 99999,
                intensity: 2,
              },
            ],
          },
        ],
      },
    ] as SkillData["event_checks"],
  },
  {
    id: "rototiller",
    description: "Lowers the speed of all enemies.",
    pokemon_type: PokemonType.GROUND,
    type: SkillType.DEBUFF,
    status: [StatusEffect.SPEED_DOWN],
    status_duration_ticks: msToTicks(5000),
    probability: 1,
    additional_vfx: [
      {
        sprite_path: "/assets/spells/tile-fx/ground-3.png",
        sprite_scale: 0.7,
        sprite_alpha: 1,
        speed: -0.5,
        anchor_to_target: true,
        y_offset: 0,
      },
    ],
  },
  {
    id: "tailwind",
    description: "Starts with 0 cooldown. Increases the speed of all allies.",
    starts_active: true,
    pokemon_type: PokemonType.FLYING,
    type: SkillType.BUFF,
    cooldown_ticks: msToTicks(4000),
    status_duration_ticks: msToTicks(5000),
    status: [StatusEffect.SPEED_UP],
    probability: 1,
    additional_vfx: [
      {
        sprite_path: "/assets/spells/vfx/hurrican-leaves__51.png",
        sprite_scale: 1.2,
        sprite_alpha: 0.85,
        speed: 0.35,
        anchor_to_target: true,
        y_offset: -45,
      },
    ],
  },
  {
    id: "sticky-web",
    description:
      "Lowers enemy speed. Stuns if the target already has speed buffs.",
    pokemon_type: PokemonType.BUG,
    type: SkillType.DEBUFF,
    status: [StatusEffect.SPEED_DOWN],
    probability: 1,

    additional_vfx: [
      {
        sprite_path: "/assets/spells/vfx/bugbuff__9.png", // TODO: change sprite
        sprite_scale: 1.5,
        sprite_alpha: 1,
        speed: 0.25,
        anchor_to_target: true,
        y_offset: -20,
      },
    ],
    event_checks: [
      {
        conditions: [
          {
            type: "TARGET_HAS_STATUS",
            status: StatusEffect.SPEED_UP,
          },
        ],
        effects: [
          {
            type: "APPLY_STATUS_TARGET",
            status_effects: [
              {
                status: StatusEffect.STUN,
                duration_ticks: msToTicks(2000),
                intensity: 1,
              },
            ],
          },
        ],
      },
    ] as SkillData["event_checks"],
  },
  {
    id: "stealth-rock",
    description: "Lowers the defense of all enemies.",
    pokemon_type: PokemonType.ROCK,
    type: SkillType.DEBUFF,
    status: [StatusEffect.DEFENSE_DOWN],
    probability: 1,
    additional_vfx: [
      {
        sprite_path: "/assets/spells/tile-fx/rock-1.png", // TODO: cools but looks like damage skill
        sprite_scale: 1.7,
        sprite_alpha: 1,
        speed: 0.65,
        anchor_to_target: true,
        y_offset: -35,
        falling: true,
      },
      {
        sprite_path: "/assets/spells/vfx/rock-impact__11.png",
        sprite_scale: 1.7,
        sprite_alpha: 1,
        speed: 0.4,
        anchor_to_target: true,
        y_offset: -35,
      },
    ],
  },
  {
    id: "curse",
    description: "Starts with 0 cooldown. Curses all enemies, applying ATTACK DOWN and 1x 🔥BURN.",
    pokemon_type: PokemonType.GHOST,
    starts_active: true,
    type: SkillType.DEBUFF,
    status: [StatusEffect.BURN, StatusEffect.ATTACK_DOWN],
    cooldown_ticks: msToTicks(11000),
    probability: 1,
    additional_vfx: [
      {
        sprite_path: "/assets/spells/tile-fx/dark-3.png",
        sprite_scale: 1,
        sprite_alpha: 0.9,
        speed: 0.4,
        anchor_to_target: true,
        y_offset: 20,
      },
    ],
  },
  {
    id: "dragon-dance",
    description: "Raises the attack of all allies.",
    starts_active: false,
    pokemon_type: PokemonType.DRAGON,
    type: SkillType.BUFF,
    status: [StatusEffect.ATTACK_UP],
    cooldown_ticks: msToTicks(7000),
    probability: 1,
    additional_vfx: [
      {
        sprite_path: "/assets/spells/vfx/roar__12.png",
        sprite_scale: 1.4,
        sprite_alpha: 1,
        speed: 0.25,
        anchor_to_target: true,
        y_offset: -55,
      },
    ],
  },
  {
    id: "snarl",
    description: "Snarls at all enemies, applying FEAR.",
    pokemon_type: PokemonType.DARK,
    type: SkillType.DEBUFF,
    status: [StatusEffect.FEAR],

    probability: 1,
    additional_vfx: [
      {
        sprite_path: "/assets/spells/tile-fx/dark-1.png",
        sprite_scale: 1.3,
        sprite_alpha: 0.95,
        speed: 0.3,
        anchor_to_target: true,
        y_offset: -45,
      },
    ],
  },
  {
    id: "gear-grind",
    description: "Grinds gears to lower all enemies' defense.",
    pokemon_type: PokemonType.STEEL,
    type: SkillType.DEBUFF,
    status: [StatusEffect.DEFENSE_DOWN],
    status_intensity: 2,
    probability: 0.7,
    additional_vfx: [
      {
        sprite_path: "/assets/spells/vfx/steelsaw__17.png",
        sprite_scale: 1.35,
        sprite_alpha: 1,
        speed: 0.3,
        anchor_to_target: true,
        y_offset: -20,
      },
    ],
  },
  {
    id: "misty-terrain",
    description: "Creates misty terrain to raise the defense of all allies.",
    pokemon_type: PokemonType.FAIRY,
    type: SkillType.BUFF,
    status: [StatusEffect.DEFENSE_UP],
    status_duration_ticks: msToTicks(5000),
    cooldown_ticks: msToTicks(11000),
    probability: 1,
    additional_vfx: [
      {
        sprite_path: "/assets/spells/vfx/pinkcharge__42.png",
        sprite_scale: 1.1,
        sprite_alpha: 0.9,
        speed: 0.25,
        anchor_to_target: true,
        y_offset: -55,
      },
    ],
  },
].map((skill) => ({
  id: skill.id,
  event_checks:
    "event_checks" in skill
      ? (skill.event_checks as SkillData["event_checks"])
      : undefined,
  momentum:
    "momentum" in skill ? (skill.momentum as SkillData["momentum"]) : undefined,
  team_conditions:
    "team_conditions" in skill
      ? (skill.team_conditions as SkillData["team_conditions"])
      : undefined,
  starts_active:
    "starts_active" in skill ? (skill.starts_active as boolean) : undefined,
  name: parseSkillIdToName(skill.id),
  description: skill.description || "",
  power: 0,
  pokemon_type: skill.pokemon_type,
  types: [skill.type],
  cooldown_ticks: skill.cooldown_ticks ?? msToTicks(5000),
  mana_cost: 10,
  range: {
    type:
      skill.type == SkillType.DEBUFF
        ? SkillRangeType.ALL_ENEMIES
        : SkillRangeType.ALL_ALLIES,
    value: 99,
  },
  charging: {
    is_required: true,
    duration_ticks: 50,
    align_to: "south",
    anim_on_end: {
      name: "pose",
      dir: "south",
    },
  },
  on_hit: [],
  on_cast: [
    {
      __typename: "Global",
      duration_ticks: 35,
      target: {
        type: skill.type == SkillType.DEBUFF ? "enemy" : "ally",
        id: 0,
      },
      status_effects: skill.status.map((status) => ({
        status: status,
        duration_ticks: skill.status_duration_ticks ?? 300,
        intensity: skill.status_intensity ?? 1,
      })),
      starting_tick: 0,
      vfx: {
        sprite_path: "/assets/spells/vfx/stars_effect_001__40.png",
        sprite_scale: 1,
        sprite_alpha: 1,
        speed: 0.5,
        anchor_to_target: true,
      },
      blocks_event_pipeline: false,
      probability: skill.probability,
    } as SkillEvent_Global,
    ...(skill.additional_vfx ?? []).map(
      (vfx) =>
        ({
          __typename: "Global",
          duration_ticks: 35,
          target: {
            type: skill.type == SkillType.DEBUFF ? "enemy" : "ally",
            id: 0,
          },
          status_effects: [],
          starting_tick: 0,
          vfx: {
            sprite_path: vfx.sprite_path,
            sprite_scale: vfx.sprite_scale,
            sprite_alpha: vfx.sprite_alpha,
            speed: vfx.speed,
            anchor_to_target: vfx.anchor_to_target,
            y_offset: vfx.y_offset,
          },

          blocks_event_pipeline: false,
        }) as SkillEvent_Global,
    ),
  ],
}));